/**
 * The alert centre (F12).
 *
 * The five signals PRD F12 names — a stalled startup, an overdue next action,
 * an overdue pilot milestone, a customer untouched for ninety days, a parked
 * startup past its revisit date — gathered into one list the team reads at the
 * weekly meeting. This is the "in-app first" half of F12; the email digest is
 * separate and optional.
 *
 * Each alert links to the record it points at, because an alert nobody can act
 * on is noise: the point is to close the loop by fixing the underlying record,
 * which clears the alert on the next load (nothing here is dismissed in place —
 * an alert is a view of the data, not a row that can drift from it).
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 9.4, PRD F12, §4.2
 */
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import type { Alert, AlertKind } from '@nexus/contracts';

import { Badge } from '../../components/ui/Badge';
import type { BadgeTone } from '../../components/ui/badge-tones';
import { Card, CardBody, CardHead } from '../../components/ui/Card';
import { Empty } from '../../components/ui/Empty';
import { Icon } from '../../components/ui/Icon';
import type { IconName } from '../../components/ui/icon-names';
import { Kicker } from '../../components/ui/Kicker';
import { PageHead } from '../../components/ui/PageHead';
import { customerPath, pilotPath, startupPath } from '../../lib/routes';

import { useAlerts } from './api/alerts.queries';

/** The icon each kind shows, from the design system's set. */
const KIND_ICON: Record<AlertKind, IconName> = {
  STALLED_STARTUP: 'clock',
  OVERDUE_ACTION: 'alert',
  OVERDUE_MILESTONE: 'checklist',
  UNTOUCHED_CUSTOMER: 'industry',
  PARKED_REVISIT_DUE: 'calendar',
};

/** The order the kinds are grouped in — the pipeline signals first. */
const KIND_ORDER: readonly AlertKind[] = [
  'STALLED_STARTUP',
  'OVERDUE_ACTION',
  'PARKED_REVISIT_DUE',
  'OVERDUE_MILESTONE',
  'UNTOUCHED_CUSTOMER',
];

export function AlertsPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { data, isPending, isError } = useAlerts();

  if (isError) {
    return (
      <p role="alert" className="text-sem-bad">
        {t('errors.generic')}
      </p>
    );
  }

  return (
    <>
      <PageHead
        kicker={t('nav.group.insight')}
        title={t('alerts.title')}
        lead={t('alerts.lead')}
      />

      {isPending ? (
        <p className="text-body" aria-busy="true">
          {t('state.loading')}
        </p>
      ) : (
        <AlertsContent alerts={data.alerts} />
      )}
    </>
  );
}

function AlertsContent({ alerts }: { alerts: readonly Alert[] }): React.JSX.Element {
  const { t } = useTranslation();

  if (alerts.length === 0) {
    return (
      <Card>
        <Empty title={t('alerts.emptyTitle')}>{t('alerts.emptyBody')}</Empty>
      </Card>
    );
  }

  /* Grouped by kind so a reader scans one category at a time — the API already
     ranks worst-first, and grouping preserves that within each kind. */
  const groups = KIND_ORDER.map((kind) => ({
    kind,
    rows: alerts.filter((alert) => alert.kind === kind),
  })).filter((group) => group.rows.length > 0);

  return (
    <div className="flex flex-col gap-s4">
      {groups.map((group) => (
        <Card key={group.kind}>
          <CardHead>
            <Kicker as="h2" count={group.rows.length}>
              {t(`alerts.kind.${group.kind}`)}
            </Kicker>
          </CardHead>
          <CardBody flush>
            <ul>
              {group.rows.map((alert) => (
                <AlertRow key={alert.id} alert={alert} />
              ))}
            </ul>
          </CardBody>
        </Card>
      ))}
    </div>
  );
}

function AlertRow({ alert }: { alert: Alert }): React.JSX.Element {
  const { t } = useTranslation();
  const tone: BadgeTone = alert.severity === 'CRITICAL' ? 'bad' : 'warn';

  return (
    <li className="border-gray-100 border-b last:border-b-0">
      <Link
        to={entityPath(alert)}
        className="hover:bg-off-white px-s3 flex items-baseline justify-between gap-3 py-2.5"
      >
        <span className="flex min-w-0 items-baseline gap-2.5">
          <Icon
            name={KIND_ICON[alert.kind]}
            className="text-body flex-none self-center"
          />
          <span className="min-w-0">
            <span className="text-navy-text text-sm font-medium">{alert.title}</span>
            <span className="text-body block truncate text-[13px] font-light">
              {alert.reason}
            </span>
          </span>
        </span>
        <Badge tone={tone}>{t(`alerts.severity.${alert.severity}`)}</Badge>
      </Link>
    </li>
  );
}

/** The detail page an alert's entity links to. */
function entityPath(alert: Alert): string {
  switch (alert.entityType) {
    case 'STARTUP':
      return startupPath(alert.entityId);
    case 'PILOT':
      return pilotPath(alert.entityId);
    case 'CUSTOMER':
      return customerPath(alert.entityId);
  }
}
